import PropTypes from 'prop-types';
import React, { createContext, useEffect, useState } from 'react';
import { StripePaymentElementFonts, StripePaymentElementStyles } from './checkout-utils';
import { useStripeInstanceContext } from './StripeInstanceContextProvider';
import { logError } from './services/logging-service';

export const StripeElementsContext = createContext(null);

export const StripeElementsContextProvider = ({
  amount,
  currency,
  country,
  onStripeElementsCreated,
  children
}) => {
  const [stripeElements, setStripeElements] = useState(null);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);

  const { stripeInstance } = useStripeInstanceContext();

  // As amount changes we need to update that amount in stripe elements instance
  useEffect(() => {
    // We can't create stripe elements without these properties
    if (!stripeInstance || !currency || !country || !amount) {
      return;
    }

    const options = {
      appearance: StripePaymentElementStyles,
      amount: Math.floor(amount * 100),
      currency: currency?.toLowerCase(),
      paymentMethodCreation: 'manual',
      mode: 'payment'
    };

    try {
      (async () => {
        // Create stripe elements only once
        if (!stripeElements) {
          const paymentRequest = stripeInstance.paymentRequest({
            country,
            currency: options.currency,
            total: {
              label: 'Peerspace',
              amount: options.amount
            }
          });

          const result = await paymentRequest.canMakePayment();

          // Convert object to array of payment options
          if (result) {
            const available = Object.keys(result || {}).filter((key) => !!result[key] && key !== 'link');

            setAvailablePaymentMethods(['card', ...available]);
          } else {
            setAvailablePaymentMethods(['card']);
          }

          const elements = stripeInstance.elements({ ...options, fonts: StripePaymentElementFonts });
          setStripeElements(elements);
          if (typeof onStripeElementsCreated === 'function') {
            onStripeElementsCreated(elements);
          }
        }

        stripeElements?.update(options);
      })();
    } catch (e) {
      logError('stripe.createOrUpdateStripeElements', e);
    }
  }, [stripeInstance, stripeElements, amount, currency, country]);

  return (
    <StripeElementsContext.Provider
      value={{
        stripeElements,
        availablePaymentMethods
      }}
    >
      {children}
    </StripeElementsContext.Provider>
  );
};

StripeElementsContextProvider.displayName = 'StripeElementsContextProvider';
StripeElementsContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  amount: PropTypes.number,
  currency: PropTypes.string,
  country: PropTypes.string,
  onStripeElementsCreated: PropTypes.func
};

export const useStripeElementsContext = () => {
  const context = React.useContext(StripeElementsContext);
  if (!context) {
    throw new Error(
      'useStripeElementsContext must be used within a StripeElementsContextProvider'
    );
  }
  return context;
};
